import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAuthContext } from '@providers/AuthProvider';
import { GetOrgMembersMemberResponseDto, JobDtoUser } from 'app/API';
import React from 'react';
import { useAssigneeCandidates } from './hooks/useAssigneeCandidates';
import TeamEmptyWarningModal from './components/TeamsEmptyWarningModal';
import TeamsFilter from './components/TeamsFilter';
import TeamTag from './components/TeamTag';
import { Content } from './GetAssigneeModal/styles/Content';
import { StyledTextarea } from './GetAssigneeModal/styles/StyledTextarea';

type AssigneeLike = GetOrgMembersMemberResponseDto | JobDtoUser;

type TProps = {
  initialValue: AssigneeLike | null;
  onCancel: () => void;
  onSuccess: (assignee: AssigneeLike | null, comment: string) => void;
};

export const GetAssigneeModal = ({ initialValue, onCancel, onSuccess }: TProps) => {
  const { organization, workspace } = useAuthContext();

  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<AssigneeLike | null>(initialValue);
  const [comment, setComment] = React.useState('');

  const [chosenTeamsFilter, setChosenTeamsFilter] = React.useState<string[]>([]);

  const userHasManageMembersPermissionForCurrentWorkspace = (organization?.userTeams ?? []).some(
    (userTeam) =>
      userTeam.manageMembers && (workspace?.Teams ?? []).some((workspaceTeam) => workspaceTeam.slug === userTeam.slug),
  );

  const {
    users,
    teams: teamsFilterValues,
    isLoadingData,
  } = useAssigneeCandidates({
    organizationId: organization?.id ?? '',
    organizationSlug: organization?.slug ?? '',
    workspaceId: workspace?.id ?? '',
    workspaceSlug: workspace?.slug ?? '',
    canManageMembers: userHasManageMembersPermissionForCurrentWorkspace,
    search,
    teamsSlugFilter: chosenTeamsFilter.join('|'),
  });

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    onSuccess(assignee, comment);
  };

  return (
    <ModalWrapper width="600px">
      <ModalTitle>Set assignee</ModalTitle>
      <Content>
        {!teamsFilterValues.length && !isLoadingData ? (
          <TeamEmptyWarningModal
            linkToWorkspacesPage={`/${organization?.slug}/workspaces/${workspace?.slug}/details`}
            workspaceTitle={String(workspace?.name)}
          />
        ) : null}

        <p className="assignedTo">{assignee ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />

        <TeamsFilter
          teamsFilterValues={teamsFilterValues}
          chosenTeamsFilter={chosenTeamsFilter}
          setChosenTeamsFilter={setChosenTeamsFilter}
          isOrganizationTeams={userHasManageMembersPermissionForCurrentWorkspace}
        />

        <TableWrapper className="customTable">
          {isLoadingData ? <AbsoluteSpinner overlay className="mt-4 h-full" /> : null}
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Teams</Th>
              </Tr>
            </Thead>
            {!isLoadingData ? (
              <Tbody>
                {users.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.name}
                      </p>
                    </Td>
                    <Td>
                      <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.email}
                      </p>
                    </Td>
                    <Td>
                      <div className="flex">
                        {(
                          data as GetOrgMembersMemberResponseDto & { teams?: { id: string; slug: string }[] }
                        )?.teams?.map((team) => (
                          <TeamTag
                            key={team.id}
                            team={team}
                            active={!chosenTeamsFilter.length || chosenTeamsFilter.includes(team.slug)}
                            onClick={() => changeAssignee(data)}
                          />
                        ))}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            ) : null}
          </Table>
        </TableWrapper>

        <p className="assignee-comment">Add a comment for assignee</p>
        <StyledTextarea
          label="Type your message here"
          name="assigneeComment"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          value={comment}
        />

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button data-testid="saveButton" onClick={handleSubmit}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
